import { HStack, IconButton, Text, type TextProps } from '@chakra-ui/react'
import pluralize from 'pluralize'
import { type FC } from 'react'

import MultiDeleteButton from './multiDeleteButton'

import { Tooltip } from '@app/components/ui/tooltip'
import useGetObjectsByIds from '@app/hooks/useGetObjectsByIds'
import BulkReassignBusinessContextPopover from '@app/pages/shared/bulkReassignBusinessContextPopover'
import type { BulkLabelableObject } from '@app/pages/strategies/components/nodes/components/toolbar/components/bulkLabels/bulkLabelsPopover'
import BulkLabelsPopover from '@app/pages/strategies/components/nodes/components/toolbar/components/bulkLabels/bulkLabelsPopover'
import Can from '@app/shared/authorization/can'
import type { DomainStrategy } from '@app/types'
import ICON_MAP from '@app/utils/iconMap'

type Props = TextProps & {
  type: 'basicCard' | 'entity' | 'metric' | 'strategy'
  selected: (string | number)[]
  onDelete?: () => void
  movable?: boolean
}

const MultiOps: FC<Props> = ({ type, selected, onDelete, movable = false, ...rest }) => {
  const count = selected.length

  const domainObjects = useGetObjectsByIds(
    type,
    selected.map((s) => s.toString())
  ) as BulkLabelableObject[]

  // The above type is overly broad, so we cast it to the specific type we expect for moving maps.
  const movableObjects = domainObjects as DomainStrategy[]

  if (count === 0) {
    return null
  }

  return (
    <HStack>
      <Text whiteSpace="nowrap" {...rest}>
        {count} {pluralize('row', count)} selected
      </Text>
      <Can I="update" a={type}>
        <Tooltip content="Add labels" showArrow>
          <span>
            <BulkLabelsPopover domainObjects={domainObjects}>
              <IconButton aria-label={`Label ${type}`} variant="ghost">
                <ICON_MAP.LabelAction />
              </IconButton>
            </BulkLabelsPopover>
          </span>
        </Tooltip>
      </Can>
      {movable && (
        <Can I="update" a={type}>
          <Tooltip content="Update folder" showArrow>
            <span>
              <BulkReassignBusinessContextPopover domainObjects={movableObjects}>
                <IconButton
                  aria-label={`Update ${type} folder`}
                  data-cy={`${type}-update-folder-button`}
                  variant="ghost"
                >
                  <ICON_MAP.Refolder />
                </IconButton>
              </BulkReassignBusinessContextPopover>
            </span>
          </Tooltip>
        </Can>
      )}
      <Can I="delete" a={type}>
        <Tooltip content="Delete" showArrow>
          <span>
            <MultiDeleteButton type={type} selected={selected} onDelete={onDelete} />
          </span>
        </Tooltip>
      </Can>
    </HStack>
  )
}

export default MultiOps
